import { useState } from "react";
import {
  BarChart3,
  Calendar,
  Cog,
  KanbanSquare,
  Loader2,
  Mail,
  Phone,
  PieChart,
  UserPen,
  type LucideIcon,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { updateProjectFeatures } from "@/api/projects";
import { useProjectStore } from "@/stores/project.store";
import type { FeatureKey, Project } from "@/api/types";

interface FeatureSpec {
  key: FeatureKey;
  icon: LucideIcon;
  label: string;
  description: string;
}

const FEATURE_LIST: FeatureSpec[] = [
  {
    key: "scoring",
    icon: BarChart3,
    label: "Scoring Engine",
    description:
      "Lead scoring with configurable rules and sub-scores. Disabling hides scoring, score breakdown, and hiring signals.",
  },
  {
    key: "lemlist",
    icon: Mail,
    label: "Lemlist Integration",
    description: "Email outreach sequences via Lemlist.",
  },
  {
    key: "engine",
    icon: Cog,
    label: "Engine",
    description:
      "Automated pipeline jobs (enrichment, outreach, sync).",
  },
  {
    key: "analytics",
    icon: PieChart,
    label: "Analytics",
    description:
      "Reporting, funnel analysis, team performance, leaderboard.",
  },
  {
    key: "calendar",
    icon: Calendar,
    label: "Calendar",
    description: "Calendar view for tasks and scheduled calls.",
  },
  {
    key: "tasks_kanban",
    icon: KanbanSquare,
    label: "Tasks Kanban",
    description: "Kanban board view for tasks.",
  },
  {
    key: "call_center",
    icon: Phone,
    label: "Call Center",
    description:
      "Power-call workspace, queue, in-app timer, inline call log. Disabling hides the page from the sidebar.",
  },
  {
    key: "enrichment",
    icon: UserPen,
    label: "Enrichment Workstation",
    description:
      "Per-prospect enrichment workflow with LinkedIn / Kaspr / Lusha quick links and an earnings rate card. Disabling hides the page + Settings rate card tab.",
  },
];

interface FeaturesSectionProps {
  project: Project;
}

/**
 * Superadmin-only feature-flag editor. One card per known feature
 * with an icon, name, description and a Switch — toggle fires an
 * immediate PATCH /projects/{slug}/features and the response is
 * merged back into the project store so every `useFeature(...)`
 * consumer re-renders without a full project refetch.
 */
export function FeaturesSection({ project }: FeaturesSectionProps) {
  const setCurrentProject = useProjectStore((s) => s.setCurrentProject);
  const setProjects = useProjectStore((s) => s.setProjects);
  const projects = useProjectStore((s) => s.projects);
  // Per-key in-flight indicator so each Switch can show its own
  // spinner while another one is being toggled.
  const [pending, setPending] = useState<Set<FeatureKey>>(new Set());

  async function toggleFeature(key: FeatureKey, next: boolean) {
    setPending((prev) => new Set(prev).add(key));
    try {
      const res = await updateProjectFeatures(project.slug, { [key]: next });
      // Merge the new features map into the live project. The PATCH
      // response only carries features_enabled (per the backend's
      // ProjectFeaturesRead shape), so we keep the rest of the
      // project untouched.
      const updated: Project = {
        ...project,
        features_enabled: res.features_enabled,
      };
      setCurrentProject(updated);
      setProjects(
        projects.map((p) => (p.slug === updated.slug ? updated : p)),
      );
      toast.success(
        next ? `${labelFor(key)} enabled` : `${labelFor(key)} disabled`,
      );
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setPending((prev) => {
        const out = new Set(prev);
        out.delete(key);
        return out;
      });
    }
  }

  return (
    <section className="space-y-5">
      <header>
        <h2 className="text-lg font-medium tracking-tight text-slate-900">
          Project Features
        </h2>
        <p className="text-sm text-slate-500">
          Control which features are available for this project. Only
          superadmins can modify these settings.
        </p>
      </header>

      <div className="space-y-3">
        {FEATURE_LIST.map((feature) => {
          const enabled = project.features_enabled?.[feature.key] !== false;
          const busy = pending.has(feature.key);
          const Icon = feature.icon;
          return (
            <div
              key={feature.key}
              className={cn(
                "flex items-center justify-between rounded-xl bg-white p-4 transition-all duration-150",
                enabled
                  ? "shadow-card-rest"
                  : "bg-slate-50/60 opacity-60",
              )}
            >
              <div className="flex items-start gap-3">
                <Icon
                  className={cn(
                    "mt-0.5 h-5 w-5 shrink-0",
                    enabled ? "text-indigo-500" : "text-slate-400",
                  )}
                />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-900">
                      {feature.label}
                    </span>
                    {!enabled && (
                      <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-500">
                        Disabled
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {feature.description}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {busy && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
                )}
                <Switch
                  checked={enabled}
                  disabled={busy}
                  onCheckedChange={(v) => void toggleFeature(feature.key, v)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function labelFor(key: FeatureKey): string {
  return FEATURE_LIST.find((f) => f.key === key)?.label ?? key;
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Couldn't update feature.";
}
